import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Rocket, Undo2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  fetchMedicationLaunch, saveMedicationAudit,
  launchMedication, unlaunchMedication,
} from "../../lib/api";

const STATUS_BADGE = {
  draft:     "bg-stone-100 text-stone-600 border-stone-300",
  in_review: "bg-amber-100 text-amber-700 border-amber-300",
  launched:  "bg-emerald-100 text-emerald-700 border-emerald-300",
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function LaunchDrawer({ med, onClose, onChanged }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    pharmacist_name: "", pharmacist_role: "PharmD", audit_date: todayISO(),
    rows_checked: "", rows_passed: "", note: "", file_url: "",
  });
  const [confirm, setConfirm] = useState(null);  // "launch" | "unlaunch" | null
  const [confirmNote, setConfirmNote] = useState("");

  async function load() {
    setLoading(true);
    try {
      const s = await fetchMedicationLaunch(med.id);
      setState(s);
      if (s.audit) {
        setForm((f) => ({
          ...f,
          pharmacist_name: s.audit.pharmacist_name || "",
          pharmacist_role: s.audit.pharmacist_role || "PharmD",
          audit_date: s.audit.audit_date || todayISO(),
          rows_checked: String(s.audit.rows_checked ?? ""),
          rows_passed: String(s.audit.rows_passed ?? ""),
          note: s.audit.note || "",
          file_url: s.audit.file_url || "",
        }));
      }
    } catch (e) {
      toast.error(`Failed to load launch state: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [med.id]);

  const audit = state?.audit || null;
  const status = state?.launch_status || "draft";
  const verifiedCount = state?.verified_count ?? 0;
  const auditPassed = !!audit?.audit_passed;
  const canLaunch = auditPassed && verifiedCount >= 1 && status !== "launched";

  async function onSaveAudit() {
    const checked = parseInt(form.rows_checked, 10);
    const passed = parseInt(form.rows_passed, 10);
    if (!form.pharmacist_name.trim()) { toast.error("Pharmacist name is required."); return; }
    if (!(checked >= 0) || !(passed >= 0)) { toast.error("Enter rows checked and rows passed."); return; }
    if (passed > checked) { toast.error("Rows passed can't exceed rows checked."); return; }
    setBusy(true);
    try {
      const r = await saveMedicationAudit(med.id, {
        pharmacist_name: form.pharmacist_name.trim(),
        pharmacist_role: form.pharmacist_role.trim() || "PharmD",
        audit_date: form.audit_date,
        rows_checked: checked,
        rows_passed: passed,
        note: form.note.trim() || null,
        file_url: form.file_url.trim() || null,
      });
      toast.success(r.audit.audit_passed ? "Audit saved — passing (≥80%)" : "Audit saved — below 80%");
      await load();
    } catch (e) {
      toast.error(`Save audit failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }

  async function onConfirm() {
    if (!confirmNote.trim()) { toast.error("A note is required."); return; }
    setBusy(true);
    try {
      if (confirm === "launch") {
        await launchMedication(med.id, confirmNote.trim());
        toast.success("Medication launched");
      } else {
        await unlaunchMedication(med.id, confirmNote.trim());
        toast.success("Medication unlaunched");
      }
      setConfirm(null); setConfirmNote("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(`${confirm} failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40"
         data-testid="launch-drawer-overlay" onClick={onClose}>
      <div className="w-full max-w-xl overflow-y-auto bg-[#fdf8f3] p-8 shadow-2xl"
           onClick={(e) => e.stopPropagation()} data-testid="launch-drawer">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-stone-900" data-testid="launch-med-name">
              {med.generic_name || state?.generic_name || med.id}
            </h2>
            <span className={`mt-2 inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide ${STATUS_BADGE[status]}`}
                  data-testid="launch-status-badge">
              {status.replace("_", " ")}
            </span>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="launch-drawer-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-lg border border-stone-200 bg-white p-4 text-sm">
              <p className="text-stone-700" data-testid="launch-verified-count">
                <strong>{verifiedCount}</strong> verified interaction row{verifiedCount === 1 ? "" : "s"}.
              </p>
              {verifiedCount > 0 && status !== "launched" && (
                <p className="mt-1 text-amber-700" data-testid="awaiting-launch-note">
                  Verified — awaiting launch (not visible to the public yet).
                </p>
              )}
            </div>

            {/* Pharmacist audit */}
            <section>
              <h3 className="mb-3 text-sm font-semibold text-stone-900">Pharmacist audit</h3>
              {audit && (
                <p className="mb-3 text-xs" data-testid="audit-summary">
                  <span className={auditPassed ? "text-emerald-700" : "text-rose-600"}>
                    Audit: {audit.rows_passed}/{audit.rows_checked}
                  </span>
                  {" · "}{audit.pharmacist_name}, {audit.pharmacist_role || "PharmD"}
                  {" · "}{audit.audit_date}
                  {" · "}{auditPassed ? "PASS" : "BELOW 80%"}
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <label className="col-span-2 text-xs text-stone-600">Pharmacist name
                  <Input value={form.pharmacist_name} data-testid="audit-name"
                    onChange={(e) => setForm({ ...form, pharmacist_name: e.target.value })} />
                </label>
                <label className="text-xs text-stone-600">Role
                  <Input value={form.pharmacist_role} data-testid="audit-role"
                    placeholder="e.g. PharmD, RPh"
                    onChange={(e) => setForm({ ...form, pharmacist_role: e.target.value })} />
                </label>
                <label className="text-xs text-stone-600">Audit date
                  <Input type="date" value={form.audit_date} data-testid="audit-date"
                    onChange={(e) => setForm({ ...form, audit_date: e.target.value })} />
                </label>
                <label className="text-xs text-stone-600">Rows checked
                  <Input type="number" min="0" value={form.rows_checked} data-testid="audit-rows-checked"
                    onChange={(e) => setForm({ ...form, rows_checked: e.target.value })} />
                </label>
                <label className="text-xs text-stone-600">Rows passed
                  <Input type="number" min="0" value={form.rows_passed} data-testid="audit-rows-passed"
                    onChange={(e) => setForm({ ...form, rows_passed: e.target.value })} />
                </label>
                <label className="col-span-2 text-xs text-stone-600">Note
                  <Input value={form.note} data-testid="audit-note"
                    onChange={(e) => setForm({ ...form, note: e.target.value })} />
                </label>
                <label className="col-span-2 text-xs text-stone-600">Audit file URL (optional)
                  <Input value={form.file_url} data-testid="audit-file-url" placeholder="https://…"
                    onChange={(e) => setForm({ ...form, file_url: e.target.value })} />
                </label>
              </div>
              <Button className="mt-3" variant="outline" onClick={onSaveAudit} disabled={busy}
                data-testid="audit-save-btn">
                Save audit
              </Button>
            </section>

            {/* Launch / Unlaunch */}
            <section className="border-t border-stone-200 pt-5">
              {status !== "launched" ? (
                <>
                  <Button disabled={!canLaunch || busy} onClick={() => { setConfirm("launch"); setConfirmNote(""); }}
                    data-testid="launch-btn">
                    <Rocket className="mr-2 h-4 w-4" /> Launch medication
                  </Button>
                  {!canLaunch && (
                    <p className="mt-2 text-xs text-stone-500" data-testid="launch-disabled-reason">
                      {verifiedCount < 1
                        ? "Needs at least one verified row."
                        : "Needs a passing pharmacist audit (≥80%)."}
                    </p>
                  )}
                </>
              ) : (
                <Button variant="outline" disabled={busy} onClick={() => { setConfirm("unlaunch"); setConfirmNote(""); }}
                  data-testid="unlaunch-btn">
                  <Undo2 className="mr-2 h-4 w-4" /> Unlaunch
                </Button>
              )}

              {confirm && (
                <div className="mt-4 rounded-lg border border-stone-300 bg-white p-4" data-testid="launch-confirm">
                  <p className="text-sm font-medium text-stone-900">
                    {confirm === "launch"
                      ? "Confirm launch — this makes verified rows public."
                      : "Confirm unlaunch — this hides all rows from the public."}
                  </p>
                  <textarea value={confirmNote} rows={2} data-testid="launch-confirm-note"
                    placeholder="Type a note for the audit log (required)"
                    className="mt-2 w-full rounded-md border border-stone-300 p-2 text-sm"
                    onChange={(e) => setConfirmNote(e.target.value)} />
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" onClick={onConfirm} disabled={busy} data-testid="launch-confirm-btn">
                      {busy ? "Working…" : `Confirm ${confirm}`}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
